import { ChevronUp, ChevronDown, Trash2, Plus, Copy, Users } from "lucide-react";
import { KbRichEditor } from "@/components/KbRichEditor";
import { SECTION_AUDIENCES } from "@/lib/kbAudiences";

const newSection = () => ({
  id: `sec_${Math.random().toString(36).slice(2, 12)}`,
  audiences: [],
  body_html: "",
  table_styles: {},
});

/** Editor for audience-segmented article sections. */
export function KbSectionsEditor({ sections, onChange, baseHtml }) {
  const list = sections || [];
  const update = (i, patch) => onChange(list.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, newSection()]);
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const toggleAud = (i, val) => {
    const cur = new Set(list[i].audiences || []);
    cur.has(val) ? cur.delete(val) : cur.add(val);
    update(i, { audiences: [...cur] });
  };
  const copyFrom = (i, source) => {
    const html = source === "__base__" ? (baseHtml || "") : (list[Number(source)]?.body_html || "");
    if (!html) return;
    if (list[i].body_html && !window.confirm("Replace this section's content with the copied content?")) return;
    update(i, { body_html: html });
  };

  return (
    <div className="space-y-3" data-testid="kb-sections-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-200">
          <Users className="h-4 w-4 text-primary" /> Audience sections
          <span className="text-xs font-normal text-slate-400 dark:text-slate-500">— shown only to the audiences you tag</span>
        </div>
        <button type="button" onClick={add} data-testid="kb-section-add"
          className="flex items-center gap-1 rounded-md border border-primary/30 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/5">
          <Plus className="h-3.5 w-3.5" /> Add section
        </button>
      </div>

      {list.length === 0 && (
        <p className="rounded-lg border border-dashed border-slate-200 dark:border-slate-800 px-3 py-4 text-center text-xs text-slate-400 dark:text-slate-500" data-testid="kb-sections-empty">
          No audience sections yet. The main content above is shown per the article's visibility. Add a section for content only certain partner types should see.
        </p>
      )}

      {list.map((s, i) => (
        <div key={s.id} data-testid={`kb-section-${i}`} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/40 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Section {i + 1}</span>
            <div className="flex items-center gap-1">
              <select data-testid={`kb-section-copy-${i}`} value="" onChange={(e) => { copyFrom(i, e.target.value); e.target.value = ""; }}
                className="h-7 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                <option value="">Copy content from…</option>
                <option value="__base__">Main content</option>
                {list.map((o, oi) => oi !== i && <option key={o.id} value={oi}>Section {oi + 1}</option>)}
              </select>
              <button type="button" onClick={() => move(i, -1)} data-testid={`kb-section-up-${i}`} className="rounded p-1 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><ChevronUp className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} data-testid={`kb-section-down-${i}`} className="rounded p-1 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><ChevronDown className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => remove(i)} data-testid={`kb-section-remove-${i}`} className="rounded p-1 text-slate-400 dark:text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
          <div className="mb-2 flex flex-wrap gap-1.5" data-testid={`kb-section-audiences-${i}`}>
            {SECTION_AUDIENCES.map((a) => {
              const on = (s.audiences || []).includes(a.value);
              return (
                <button key={a.value} type="button" onClick={() => toggleAud(i, a.value)}
                  data-testid={`kb-section-aud-${i}-${a.value}`}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                    on ? "border-primary bg-primary/10 text-primary" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                  {a.short}
                </button>
              );
            })}
          </div>
          {(!s.audiences || !s.audiences.length) && (
            <p className="mb-2 text-[11px] text-amber-600">No audience selected — this section stays hidden from everyone until you tag it.</p>
          )}
          <KbRichEditor value={s.body_html} onChange={(html) => update(i, { body_html: html })} />
        </div>
      ))}
    </div>
  );
}
